import { type FC } from "react";
import { FormProvider, type UseFormReturn, useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { CODE_LANG_OPTIONS } from "../../../shared/worksheets/code-kernels";
import { LateSubmissionIntervalDropdown } from "../LateSubmissionIntervalDropdown";

import type { GeneralFormValues } from "./types";

import { LANGUAGE_LABELS, SupportedLanguage, t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { CircleInfoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectDesignatedSection, selectIsAiAssessmentEnabledInOrg } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { PROJECTOR_ZOOM_ITEMS } from "utils/presentation";
import { isLinkedSection } from "utils/publishing";
import { isAssessmentOnlyEnabled } from "utils/waffle-flags";

interface GeneralSettingsFormProps {
  className?: string;
  form: UseFormReturn<GeneralFormValues>;
}

export const GeneralSettingsForm: FC<GeneralSettingsFormProps> = ({ className, form }) => {
  const designatedSection = useObjectSelector(selectDesignatedSection, [
    "id",
    "kind",
    "term_id",
    "template_id",
    "archived_at",
  ]);
  const isAiAssessmentEnabledInOrg = useAppSelector(selectIsAiAssessmentEnabledInOrg);
  const { isArchived } = useIsArchived();

  const { field: daysLateField } = useController({ name: "daysLate", control: form.control });

  if (!designatedSection) {
    return null;
  }

  // Archived sections are read-only, so all settings are disabled (like linked sections).
  const disabled = isLinkedSection(designatedSection) || isArchived(designatedSection);

  const codeBlocksDefaultLanguage = form.watch("code_blocks_default_language");
  const codeBlocksEnabled = form.watch("code_blocks_enabled");
  const projectorZoom = form.watch("projector_zoom");
  const sectionLanguage = form.watch("section_language");
  const formAllowLateSubmissions = form.watch("allowLateSubmissions");

  const unspecifiedLanguageId = "unspecified";
  const languageOptions = Object.entries(LANGUAGE_LABELS)
    .map(([id, label]) => ({
      id,
      title: label,
    }))
    .concat([{ id: unspecifiedLanguageId, title: t("settings.section_language_unspecified") }]);

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
      >
        <div className="flex flex-col gap-3">
          <FormField
            disabled={disabled}
            helper={t("settings.section_language_tooltip")}
            label={t("settings.section.section_language")}
            name="section_language"
            required
          >
            {(controlProps) => (
              <Listbox
                {...controlProps}
                eventName="Course language changed"
                eventPropertyKey="newLanguage"
                items={languageOptions}
                selectedValue={sectionLanguage || "unspecified"}
                onSelectionChange={({ id }) =>
                  form.setValue("section_language", id as SupportedLanguage, {
                    shouldDirty: true,
                  })
                }
              />
            )}
          </FormField>

          <Checkbox
            {...form.register("student_ai_assistant_enabled", { disabled })}
            eventName="Section Settings - Student AI chat toggle"
          >
            {t("settings.section.student_ai_assistant_enabled")}
          </Checkbox>

          {isAiAssessmentEnabledInOrg ? (
            <Checkbox
              {...form.register("ai_assessment_enabled", { disabled })}
              eventName="Section Settings - AI assessment toggle"
            >
              {t("settings.section.ai_assessment_enabled")}
            </Checkbox>
          ) : (
            <Tooltip content={t("settings.section.ai_assessment_enabled_org_disabled_tooltip")}>
              <Checkbox {...form.register("ai_assessment_enabled", { disabled: true })}>
                {t("settings.section.ai_assessment_enabled")}
              </Checkbox>
            </Tooltip>
          )}

          <Checkbox
            {...form.register("anonymous_grading_enabled", { disabled })}
            eventName="Section Settings - Anonymous grading toggle"
          >
            {t("settings.section.anonymous_grading_enabled")}
          </Checkbox>

          <Tooltip content={t("settings.hiding_static_blocks_tooltip")}>
            <Checkbox
              {...form.register("hide_static_blocks_on_student_response_page", { disabled })}
              eventName="Section Settings - Non-interactive blocks toggle"
            >
              {t("settings.section.hide_static_blocks_on_student_response_page")}
            </Checkbox>
          </Tooltip>

          <Checkbox
            {...form.register("code_blocks_enabled", { disabled })}
            eventName="Section Settings - Code blocks toggle"
          >
            {t("settings.section.code_blocks_enabled")}
          </Checkbox>

          <Checkbox
            {...form.register("should_lock_pages_at_due_date", { disabled })}
            eventName="Section Settings - Lock pages at due date toggle"
          >
            {t("settings.section.lock_pages_at_due_date")}
          </Checkbox>

          <div className="flex items-center gap-1">
            <Checkbox
              {...form.register("allowLateSubmissions", { disabled })}
              eventName="Section Settings - Allow late submissions toggle"
            >
              {t("settings.section.allow_late_submissions")}
            </Checkbox>
            <TooltipTrigger content={t("settings.section.allow_late_submissions_tooltip")}>
              <CircleInfoIcon className="text-black-tint-40" size="s" />
            </TooltipTrigger>
          </div>

          <FormField
            disabled={!formAllowLateSubmissions || disabled}
            label={t("settings.section.grace_period")}
            name="daysLate"
            required
          >
            {(controlProps) => (
              <LateSubmissionIntervalDropdown
                {...controlProps}
                value={daysLateField.value}
                onChange={daysLateField.onChange}
              />
            )}
          </FormField>

          <FormField
            disabled={!codeBlocksEnabled || disabled}
            label={t("settings.default_code_block_language")}
            name="code_blocks_default_language"
            required
          >
            {(controlProps) => (
              <Listbox
                {...controlProps}
                eventName="Section Settings - Programming language changed"
                eventPropertyKey="newLanguage"
                items={CODE_LANG_OPTIONS}
                selectedValue={codeBlocksDefaultLanguage}
                onSelectionChange={({ id }) =>
                  form.setValue("code_blocks_default_language", id, { shouldDirty: true })
                }
              />
            )}
          </FormField>

          {!isAssessmentOnlyEnabled() && (
            <FormField
              disabled={disabled}
              label={t("settings.projector_zoom")}
              name="projector_zoom"
              required
            >
              {(controlProps) => (
                <Listbox
                  {...controlProps}
                  eventName="Section Settings - Projector zoom changed"
                  eventPropertyKey="zoom"
                  items={PROJECTOR_ZOOM_ITEMS}
                  selectedValue={projectorZoom.toString()}
                  onSelectionChange={({ id }) =>
                    form.setValue("projector_zoom", parseInt(id, 10), { shouldDirty: true })
                  }
                />
              )}
            </FormField>
          )}
        </div>
      </div>
    </FormProvider>
  );
};
